import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram, Building2, Youtube } from 'lucide-react';
import { useSettings } from '../context/SettingsContext'; // Importar useSettings
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../context/AuthContext';

const Footer = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { getCurrentBranding, getThemeClasses } = useBranding();
  const { currentTenant } = useAuth();
  
  const currentBranding = getCurrentBranding();

  return (
    <motion.footer
      className="bg-gray-900 text-gray-300 py-10 mt-12"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Columna 1: Logo y Descripción */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left">
          <motion.div 
            className="flex items-center gap-2 mb-4"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {currentBranding.logo ? (
              <img src={currentBranding.logo} alt="Company Logo" className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className={`w-10 h-10 ${getThemeClasses().gradient} rounded-xl flex items-center justify-center`}>
                <Building2 className="w-6 h-6 text-white" />
              </div>
            )}
            <span className="text-2xl font-bold text-white">{currentBranding.name}</span>
          </motion.div>
          
          {/* Logo y Nombre de Franquicia */}
          {currentBranding.franchise_logo && (
            <motion.div 
              className="flex items-center gap-2 mt-4"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <img 
                src={currentBranding.franchise_logo} 
                alt={currentBranding.franchise_name || 'Franquicia'} 
                className="w-10 h-10 rounded-xl object-cover" 
              />
              {currentBranding.franchise_name && (
                <span className="text-2xl font-bold text-white">{currentBranding.franchise_name}</span>
              )}
            </motion.div>
          )}
        </div>

        {/* Columna 2: Información de Contacto */}
        <div className="text-center md:text-left">
          <h3 className="text-lg font-semibold text-white mb-4">{t('common.contact') || 'Contáctanos'}</h3>
          <ul className="space-y-3">
            {currentBranding.phone && (
              <li className="flex items-center justify-center md:justify-start gap-3">
                <Phone className="w-5 h-5 text-blue-400" />
                <span>{currentBranding.phone}</span>
              </li>
            )}
            {currentBranding.email && (
              <li className="flex items-center justify-center md:justify-start gap-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <a href={`mailto:${currentBranding.email}`} className="hover:text-blue-400 transition-colors">
                  {currentBranding.email}
                </a>
              </li>
            )}
            {currentBranding.address && (
              <li className="flex items-center justify-center md:justify-start gap-3">
                <MapPin className="w-5 h-5 text-blue-400" />
                <span>{currentBranding.address}</span>
              </li>
            )}
          </ul>
        </div>

        {/* Columna 3: Redes Sociales */}
        <div className="text-center md:text-left">
          <h3 className="text-lg font-semibold text-white mb-4">{t('common.follow') || 'Síguenos'}</h3>
          <div className="flex justify-center md:justify-start gap-4">
            {currentBranding.social?.facebook && (
              <motion.a 
                href={currentBranding.social.facebook} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <Facebook className="w-7 h-7" />
              </motion.a>
            )}
            {currentBranding.social?.twitter && (
              <motion.a 
                href={currentBranding.social.twitter} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <Twitter className="w-7 h-7" />
              </motion.a>
            )}
            {currentBranding.social?.linkedin && (
              <motion.a 
                href={currentBranding.social.linkedin} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <Linkedin className="w-7 h-7" />
              </motion.a>
            )}
            {currentBranding.social?.instagram && (
              <motion.a 
                href={currentBranding.social.instagram} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <Instagram className="w-7 h-7" />
              </motion.a>
            )}
            {currentBranding.social?.youtube && (
              <motion.a 
                href={currentBranding.social.youtube} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <Youtube className="w-7 h-7" />
              </motion.a>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} {currentBranding.name}. Todos los derechos reservados.
        <br />
        <span className="text-xs">{currentBranding.footerText}</span>
      </div>
    </motion.footer>
  );
};

export default Footer;